import * as s from "../signin/LoginStyled";
import * as cs from "../SignCommonStyled";
import {
  ResendCodePopup,
  VERIFY_TYPE,
  VerifyTypeString,
} from "pages/auth/verify2fa/resendCode/resendCode";
import { LOGIN_STEP } from "../signin/LoginContainer";
import VerifyTitle from "components/blocks/verify/VerifyTitle";
import VerfiyCode from "components/blocks/verify/VerfiyCode";
import { Text, UnderlineText, ValidateWord } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  LoginResponse,
  Verify2faRequest,
  VerifyInfoType,
} from "api/interfaces/authInterface.interface";
import { useCallback, useEffect, useRef, useState } from "react";
import { postResendCode, postVerify2fa } from "api/authAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "components/Auth";
import { ACCESS_TOKEN } from "api/axiosInstance";
import { getCookie } from "cookies/cookie";
import { onSuccessLogin } from "utils/AuthUtil";
import useStatusMutation from "hook/useStatusMutation";
import useMutationButton, {
  MutationButtonProps,
} from "components/atoms/buttons/useMutationButton";
import { useNoticeHeight } from "components/NoticePovider";
import { useMenu } from "hook/useMenu";
import useRegisterPasskey from "hook/useRegisterPasskey";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { onChangeRead } from "redux/reducers/announcement/announcementSlice";

export default function Verify2FA(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const { navigateSwitchAccount } = useMenu({});

  const themeMode = useAppSelector((state) => state.theme.theme);

  const location = useLocation();
  const state = location.state;
  const { noticeHeight } = useNoticeHeight();

  const loginInfo = state?.loginInfo ?? { ...auth.user };
  const dispatch = useAppDispatch();

  // console.log(state, loginInfo);
  const [isResendCodePopup, setIsResendCodePopup] = useState<boolean>(false);
  const [trustDevice, setTrustDevice] = useState<boolean>(false);

  const [verifyType, setVerifyType] = useState<VERIFY_TYPE>(
    VERIFY_TYPE[state?.verifyType as VerifyTypeString] ?? VERIFY_TYPE.EMAIL
  );
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [mfaToken, setMfaToken] = useState<string>("");
  const { displayError, intlString, lang, notifySuccess } =
    useApiError();

  const [isValidated, setIsValidated] = useState<Validate>({
    resend: false,
    verifyCode: false,
  });

  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    resend: "",
    verifyCode: "",
  });

  const onChangeVerifyCode = useCallback(
    (value: string) => {
      setVerifyCode(value);
    },
    [verifyCode, setVerifyCode]
  );
  const { onRegisterPasskey } = useRegisterPasskey({
    onSuccess: () => {
      navigate("/login", {
        state: {
          step: LOGIN_STEP.LOGIN_WITH_PASSKEY,
        },
      });
    },
    onFail: () => {
      navigate("/login");
    },
  });

  const onErrorVerify = (msg?: string) => {
    setIsValidated({ ...isValidated, verifyCode: true });
    setValidateMsg({
      ...validateMsg,
      verifyCode: msg ? msg : intlString(lang.validateMsg.signup.verifyNumber),
    });
  };

  const mutationResendCode = useStatusMutation(postResendCode, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        if (mfaToken) {
          notifySuccess(lang.label.videoshare.guest.verify.resend.success);
        }
        if (!state?.isAuth) {
          setMfaToken(res.result.mfaToken);
        }

        setIsResendCodePopup(false);
      } else {
        setIsValidated({ ...isValidated, resend: true });
        setValidateMsg({
          ...validateMsg,
          resend: intlString(lang.validateMsg.signup.noExistEmail),
        });
      }
    },
    onError: () => {
      setIsValidated({ ...isValidated, resend: true });
      setValidateMsg({
        ...validateMsg,
        resend: intlString(lang.validateMsg.signup.noExistEmail),
      });
    },
  });

  const send2faButtonProps: MutationButtonProps = {
    ButtonComponent: cs.BigButton,
    onClick: (mutation) => {
      let body: Verify2faRequest = {
        email: loginInfo.email,
        accessToken: state?.isAuth ? getCookie(ACCESS_TOKEN) : mfaToken,
        code: verifyCode,
        isAuth: state?.isAuth ?? true,
        checkedRemember2fa: trustDevice,
      };

      mutation.mutate({
        payload: body,
      });
    },
    mutationFn: postVerify2fa,
    onSuccess: (data) => {
      // console.log(state);
      if (trustDevice && data.result.remember2fa) {
        localStorage.setItem(
          "remember2fa_" + loginInfo.email,
          data.result.remember2fa
        );
      }

      if (!!state?.setupPasskey) {
        onRegisterPasskey(loginInfo.email);
        return;
      }
      auth.updateVerify2fa(true);
      if (!state?.isAuth && state?.videoInfo) {
        const searchParams = new URLSearchParams({
          videoId: state.videoInfo.videoId,
          userId: state.videoInfo.userId,
        }).toString();
        return navigate(`/guest/video/player?${searchParams}`, {
          state: {
            isVerified: true,
            tokenInfo: state.videoInfo,
          },
        });
      }
      if (state?.isLogin) {
        onLogin(state.isLogin as LoginResponse);
        return;
      }
      if (state?.selectAccount?.accountId) {
        navigateSwitchAccount(
          {
            accountId: state.selectAccount.accountId,
            replace: true,
            isVerified: true,
          },
          false
        );
      }
    },
    onError: (e: any) => {
      onErrorVerify(e.response.data.errorString);
    },
  };
  const { MutationButton, handleClick: onClickOtpEnter } =
    useMutationButton(send2faButtonProps);

  const onMutateResendCode = (type: VERIFY_TYPE) => {
    if (!mutationResendCode.idle) return;
    // console.log("onMutateResendCode");
    setIsValidated({ resend: false, verifyCode: false });
    setValidateMsg({
      resend: "",
      verifyCode: "",
    });
    setVerifyCode("");
    mutationResendCode.mutate({
      payload: state?.isAuth
        ? {
            isAuth: state.isAuth ?? true,
            accessToken: getCookie(ACCESS_TOKEN),
            twoFactorType: type,
            email: loginInfo.email,
          }
        : {
            isAuth: state?.isAuth ?? true,
            twoFactorType: type,
            email: loginInfo.email,
          },
    });
  };

  const onLogin = (loginData: LoginResponse) => {
    const toSearchParam = new URLSearchParams(location.state?.search);
    const onError = (err?: CustomError) => {
      setIsValidated({ ...isValidated, password: true });
      setValidateMsg({
        ...validateMsg,
        password: displayError(
          err as CustomError,
          intlString(lang.validateMsg.signup.loginIncorrect)
        ),
      });
    };
    if (!toSearchParam.get("r")) {
      toSearchParam.set("r", loginInfo.memberKey);
      toSearchParam.set("account", loginInfo.accou);
    }
    onSuccessLogin(auth, loginInfo.email, loginData, onError, toSearchParam);
  };

  const onClickVerifyResend = useCallback(
    (type: VERIFY_TYPE) => {
      // console.log(type, "type");
      setVerifyType(type);
      onMutateResendCode(type);
    },
    [setVerifyType, onMutateResendCode, loginInfo.email, ACCESS_TOKEN]
  );

  const onClickResend = useCallback(() => {
    if (!state?.isAuth) {
      onClickVerifyResend(VERIFY_TYPE.EMAIL);
      return;
    }
    setIsResendCodePopup(true);
  }, [state?.isAuth, onClickVerifyResend, setIsResendCodePopup]);

  const onChangeTrustDevice = useCallback(
    (e: any) => {
      setTrustDevice(e.currentTarget.checked);
    },
    [setTrustDevice]
  );

  useEffect(() => {
    if (isInitialRender.current) {
      // 첫 렌더링일 때만 실행
      if (!mutationResendCode.idle) return;
      onMutateResendCode(verifyType);
      isInitialRender.current = false;
    }
  }, [isInitialRender, mutationResendCode.idle, verifyType]);

  return (
    <>
      <cs.BG noticeHeight={noticeHeight}>
        <cs.Container>
          <cs.LoginBox>
            <VerifyTitle
              title="Two-factor Authentication"
              desc={intlString(
                verifyType === VERIFY_TYPE.EMAIL
                  ? lang.enter_pin_message
                  : lang.enter_pin_message_phone
              )}
            />

            <VerfiyCode
              verifyCode={verifyCode}
              onChange={onChangeVerifyCode}
              onClickOtpEnter={onClickOtpEnter}
            />

            <cs.CenterBox>
              <UnderlineText
                fontSize={14}
                color={themeMode === "light" ? COLORS.COMPANY : COLORS.BUTTONS2}
                onClick={onClickResend}
                data-testid="auth-verify2fa-verify2fa-underline-text-click-resend"
              >
                Resend Code
              </UnderlineText>
              {state?.isLogin && (
                <s.CheckboxInput htmlFor="login-form-trust-device">
                  <input
                    type="checkbox"
                    id="login-form-trust-device"
                    className=""
                    defaultChecked={trustDevice}
                    onChange={onChangeTrustDevice}
                    data-testid="auth-verify2fa-verify2fa-login-form-trust-device-change-trust-device"
                  />

                  <Text fontSize={12}>Trust this device for 30 days</Text>
                </s.CheckboxInput>
              )}
            </cs.CenterBox>
            <cs.ValidateWrapper>
              <ValidateWord data-testid="auth-verify2fa-verify2fa-validate-verify-code">
                {validateMsg.verifyCode}
              </ValidateWord>
            </cs.ValidateWrapper>
            <MutationButton>Verify</MutationButton>
          </cs.LoginBox>
        </cs.Container>
      </cs.BG>
      {isResendCodePopup && (
        <ResendCodePopup
          {...(loginInfo as VerifyInfoType)}
          verifyType={verifyType}
          onClickCancel={() => {
            setIsResendCodePopup(false);
          }}
          onClickSend={onClickVerifyResend}
        />
      )}
    </>
  );
}
